import { lazy } from "react";

/**
 * This file handles all the lazy imports for components used across the project.
 * Components are loaded on demand to optimize the initial load time.
 */

// -------------------- Shared --------------------

/** Outlet component for dynamically rendering shared components */
export const ComponentOutlet = lazy(
  () => import("../components/shared/component-outlet")
);

// -------------------- Traffic Light --------------------

/** Form for creating or editing traffic light records */
export const TrafficLightsForm = lazy(
  () => import("../components/traffic-light/traffic-light-form")
);

/** View for displaying traffic light details */
export const TrafficLightDetails = lazy(
  () =>
    import(
      "../components/traffic-light/traffic-light-details/traffic-light-details"
    )
);

// -------------------- Clients --------------------

/** Form for creating or editing a client */
export const ClientForm = lazy(
  () => import("../components/clients/clients-form")
);

/** Form for creating a VDI client */
export const VdiClientForm = lazy(
  () => import("../components/clients/vdi-clients-form")
);

/** View for displaying client details */
export const ClientDetails = lazy(
  () => import("../components/clients/client-details/client-details")
);

// -------------------- Buildings --------------------

/** View for displaying building details */
export const BuildingDetails = lazy(
  () => import("../components/buildings/building-details/building-details")
);

/** Form for creating or editing a building */
export const BuildingsForm = lazy(
  () => import("../components/buildings/buildings-form")
);

// -------------------- Contacts --------------------

/** View for displaying contact details */
export const ContactDetails = lazy(
  () => import("../components/contacts/contact-details/contact-details")
);


/** Form for creating or editing a contact */
export const ContactsForm = lazy(
  () => import("../components/contacts/contacts-form")
);

// -------------------- Plants --------------------

/** Form for creating or editing a plant */
export const PlantsForm = lazy(
  () => import("../components/plants/plants-form")
);

/** View for displaying plant details */
export const PlantDetails = lazy(
  () => import("../components/plants/plant-details/plant-details")
);

// -------------------- Tasks --------------------

/** List view of all tasks */
export const TasksList = lazy(() => import("../components/tasks/tasks-list"));

/** Form for creating or editing a task */
export const TasksForm = lazy(() => import("../components/tasks/tasks-form"));

/** List view of drinking water tasks */
export const DrinkingWaterTasksList = lazy(() => import("../components/tasks/drinking-water-tasks-list"));

/** Search view over every sub-task the user is entitled to see */
export const TaskSearchList = lazy(() => import("../components/tasks/task-search-list"));

/** List view of all drinking water buildings */
export const AllDrinkingWaterBuildingsList = lazy(() => import("../components/buildings/all-drinking-water-buildings-list"));

/** View for displaying task details */
export const TaskDetails = lazy(
  () => import("../components/tasks/task-details/task-details")
);

/** List of resolved tasks */
export const ResolvedTasksList = lazy(
  () => import("../components/tasks/resolved-tasks-list")
);

/** Task settings list */
export const TaskSettingsList = lazy(
  () => import("../components/task-settings/task-settings-list")
);

/** Details view for task settings */
export const TaskSettingsDetails = lazy(
  () => import("../components/task-settings/task-settings-details")
);

// -------------------- Client Visits --------------------

/** Form for creating or editing visit tasks */
export const VisitTaskForm = lazy(
  () => import("../components/visit-tasks/visit-task-form")
);

/** List view of visit tasks */
export const VisitTaskList = lazy(
  () => import("../components/visit-tasks/visit-list")
);

/** View for displaying visit task details */
export const VisitTaskDetails = lazy(
  () => import("../components/visit-tasks/visit-task-details")
);

// -------------------- Sub Tasks --------------------

/** List view of all sub-tasks */
export const SubTasksList = lazy(
  () => import("../components/sub-tasks/sub-tasks-list")
);

/** Form for creating or editing a sub-task */
export const SubTasksForm = lazy(
  () => import("../components/sub-tasks/sub-tasks-form")
);

/** View for displaying sub-task details */
export const SubTaskDetails = lazy(
  () => import("../components/sub-tasks/sub-tasks-details/sub-task-details")
);

export const PlantVisitTaskDetails = lazy(
  () => import("../components/sub-tasks/history/plant-visit-task-details")
);

/** List of resolved sub-tasks */
export const ResolvedSubTasksList = lazy(
  () => import("../components/sub-tasks/history/resolved-sub-tasks-list")
);

/** List of onsite sub-tasks */
export const OnsiteSubTasksList = lazy(
  () => import("../components/sub-tasks/history/onsite-sub-tasks-list")
);

/** List of resolved microbiological sub-tasks */
export const ResolvedMicroSubTasksList = lazy(
  () => import("../components/sub-tasks/history/resolved-micro-sub-tasks-list")
);

/** List of lab sub-tasks */
export const LabSubTasksList = lazy(
  () => import("../components/sub-tasks/history/lab-sub-tasks-list")
);

/** Yearly PDF export page for sub-tasks */
export const YearlyPdfPage = lazy(
  () => import("../components/sub-tasks/history/yearly-pdf")
);

// -------------------- Disturbances --------------------

/** List view of disturbances */
export const DisturbancesList = lazy(
  () => import("../components/disturbances/disturbances-list")
);

/** View for displaying disturbance details */
export const DisturbanceDetails = lazy(
  () =>
    import("../components/disturbances/disturbance-details/disturbance-details")
);

/** Form for creating a new disturbance */
export const CreateDisturbance = lazy(
  () => import("../components/disturbances/create-disturbance")
);

/** Form for updating an existing disturbance */
export const UpdateDisturbance = lazy(
  () => import("../components/disturbances/update-disturbance")
);

/** List of resolved disturbances */
export const ResolvedDisturbanceList = lazy(
  () => import("../components/disturbances/resolved-disturbance-list")
);

/** List of actions */
export const ActionsList = lazy(
  () => import("../components/actions/actions-list")
);

/** Form for creating or editing an action */
export const ActionsForm = lazy(
  () => import("../components/actions/actions-form")
);

/** View for displaying action details */
export const ActionDetails = lazy(
  () => import("../components/actions/action-details/action-details")
);

// -------------------- Products --------------------

/** Form for creating or editing a product */
export const ProductForm = lazy(
  () => import("../components/products/products-form")
);

/** View for displaying product details */
export const ProductDetails = lazy(
  () => import("../components/products/product-details/product-details")
);

// -------------------- Plant Products --------------------

/** List view of plant products */
export const PlantProductsList = lazy(
  () => import("../components/plant-products/plant-products-list")
);

/** Form for creating or editing a plant product */
export const PlantProductForm = lazy(
  () => import("../components/plant-products/plant-products-form")
);

/** View for displaying plant product details */
export const PlantProductDetails = lazy(
  () =>
    import(
      "../components/plant-products/plant-product-details/plant-product-details"
    )
);

// -------------------- Components --------------------

/** List view of components */
export const ComponentsList = lazy(
  () => import("../components/components/components-list")
);

/** Form for creating or editing a component */
export const ComponentsForm = lazy(
  () => import("../components/components/components-form")
);

/** View for displaying component details */
export const ComponentDetails = lazy(
  () => import("../components/components/component-details/component-details")
);

// -------------------- Roles --------------------

/** Form for creating a new role */
export const CreateRole = lazy(() => import("../components/roles/create-role"));

/** View for displaying role details */
export const RoleDetails = lazy(
  () => import("../components/roles/role-details")
);

/** Form for updating an existing role */
export const UpdateRole = lazy(() => import("../components/roles/update-role"));

// -------------------- Users --------------------

/** Form for creating or editing a user */
export const UsersForm = lazy(() => import("../components/users/users-form"));

/** View for displaying user details */
export const UserDetails = lazy(
  () => import("../components/users/user-details")
);

/** Form for changing user information */
export const UsersCahngeForm = lazy(
  () => import("../components/users/users-change-form")
);

export const ClientsUsersList = lazy(
  () => import("../components/users/clients-users-list")
);
